/** Adapter between the API payment (raw Prisma + client.name) and the UI `Payment`. */

import type { Currency, Payment, PaymentMethod, PaymentStatus, PaymentType } from "@/types";

export interface ApiPayment {
  id: string;
  clientId: string;
  type: PaymentType;
  amount: number | string;
  currency: Currency;
  method?: PaymentMethod | null;
  status: PaymentStatus;
  note?: string | null;
  period?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  deletedAt?: string | null;
  receivedAt?: string | null;
  dueDate?: string | null;
  client?: { name?: string | null } | null;
}

/** Form-level input shared by create/update (UI knows the client name). */
export interface PaymentInput {
  clientId: string;
  clientName: string;
  type: PaymentType;
  amount: number;
  method?: PaymentMethod;
  status: PaymentStatus;
  note?: string;
  period?: string;
  dueDate?: string;
}

function toNumber(value: number | string | null | undefined): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(n) ? n : 0;
}

function formatDate(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return undefined;
  const p = (value: number) => String(value).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function toPayment(api: ApiPayment): Payment {
  return {
    id: api.id,
    clientId: api.clientId,
    clientName: api.client?.name ?? api.clientId,
    type: api.type,
    amount: toNumber(api.amount),
    currency: api.currency ?? "DZD",
    method: api.method ?? undefined,
    status: api.status,
    note: api.note ?? undefined,
    period: api.period ?? undefined,
    dueDate: formatDate(api.dueDate),
    receivedAt: formatDate(api.receivedAt),
    createdAt: formatDate(api.createdAt),
    updatedAt: formatDate(api.updatedAt),
    deletedAt: formatDate(api.deletedAt),
    date: formatDate(api.receivedAt ?? api.createdAt) ?? "-",
  };
}

/** Build the API create/update body from form input (UI-only fields dropped). */
export function toPaymentWriteBody(input: PaymentInput): Record<string, unknown> {
  return {
    clientId: input.clientId,
    type: input.type,
    amount: input.amount,
    currency: "DZD",
    ...(input.method ? { method: input.method } : {}),
    status: input.status,
    ...(input.period ? { period: input.period } : {}),
    ...(input.dueDate ? { dueDate: input.dueDate } : {}),
    ...(input.note ? { note: input.note } : {}),
  };
}
